import {
  Component,
  OnInit,
  inject,
  signal,
  computed,
  OnDestroy,
  PLATFORM_ID,
  ChangeDetectionStrategy,
  DestroyRef,
  ElementRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { LoadingService, School } from '@awo-care/shared-core';
import {
  EmailSuggestionHintComponent,
  FormErrorComponent,
  SchoolAutocompleteComponent,
  emailFormatValidator,
  isSchool,
  schoolDisplayName,
  schoolSelectedValidator,
} from '@awo-care/shared-ui';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { ContactChild, ContactService } from './contact.service';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { findFaqItemById } from '../faq/faq.data';
import { CALLBACK_PATTERNS, FAQ_HINT_RULES } from '../faq/faq-hint.rules';
import {
  FaqHintRule,
  matchFaqHintRules,
  matchesCallbackWithoutDetails,
} from '../faq/faq-hint.utils';
import { SchoolService } from '@awo-care/schools';
import {
  buildReferenceNumber,
  normalizeReferenceNumberBody,
  referenceNumberValidator,
} from './reference-number.utils';
import { FIREBASE_AUTH } from '@awo-care/shared-firebase';

@Component({
  selector: 'app-contact-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSnackBarModule,
    FormErrorComponent,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    RouterLink,
    EmailSuggestionHintComponent,
    SchoolAutocompleteComponent,
  ],
  templateUrl: './contact-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './contact-form.component.scss',
})
export class ContactFormComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private auth = inject(FIREBASE_AUTH);
  private recaptchaV3Service = inject(ReCaptchaV3Service);
  private schoolService = inject(SchoolService);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private destroyRef = inject(DestroyRef);
  loadingService = inject(LoadingService);
  contactService = inject(ContactService);
  router = inject(Router);
  recaptchaToken = signal<string | null>(null);
  loggedInUserEmail = signal<string | null>(null);
  schools = signal<School[]>([]);
  private recaptchaSubscription: Subscription | null = null;
  private messageSubscription: Subscription | null = null;
  screenReaderAnnouncement = '';

  /** Kennung des Rückruf-Hinweises — er verweist auf keine FAQ-Frage. */
  private static readonly CALLBACK_HINT_ID = 'callback-without-details';

  /** Regeln, die auf die aktuelle Nachricht passen (entprellt, nur im Browser). */
  private readonly matchedHintRules = signal<readonly FaqHintRule[]>([]);
  private readonly callbackHintMatches = signal(false);
  /** Weggeklickte Hinweise — sie kommen für diese Eingabe nicht wieder. */
  private readonly dismissedHintIds = signal<ReadonlySet<string>>(new Set());

  /** Mehr als drei Fragen in einer Karte liest niemand mehr. */
  private static readonly MAX_HINT_LINKS = 3;

  /** Die FAQ-Fragen, auf die der Hinweis verlinkt (ohne Dubletten). */
  readonly hintLinks = computed(() => {
    const dismissed = this.dismissedHintIds();
    const links: { faqId: string; question: string }[] = [];
    for (const rule of this.matchedHintRules()) {
      if (dismissed.has(rule.id)) {
        continue;
      }
      for (const faqId of rule.faqIds) {
        const item = findFaqItemById(faqId);
        if (!item || links.some((link) => link.faqId === faqId)) {
          continue;
        }
        links.push({ faqId, question: item.question });
      }
    }
    return links.slice(0, ContactFormComponent.MAX_HINT_LINKS);
  });

  readonly showCallbackHint = computed(
    () =>
      this.callbackHintMatches() &&
      !this.dismissedHintIds().has(ContactFormComponent.CALLBACK_HINT_ID)
  );

  readonly showHintCard = computed(
    () => this.hintLinks().length > 0 || this.showCallbackHint()
  );

  private static readonly CHILD_PREFIX = 'child:';
  readonly subjectGeneral = 'general';
  readonly subjectOtherSchool = 'other-school';

  /**
   * Schulabfrage (#1145): Eingeloggte Eltern mit Kindern wählen das Kind,
   * die Schule kommt dann von dort. Alle anderen suchen die Schule.
   * `loading` gilt, bis feststeht, welcher Fall vorliegt.
   */
  readonly schoolMode = signal<'loading' | 'search' | 'children'>('loading');
  readonly children = signal<ContactChild[]>([]);
  /** Schulsuche unter der Kind-Auswahl — bei „Andere Schule“ oder Kind ohne Schule. */
  readonly showChildSchoolSearch = signal(false);
  /** Das gewählte Kind hat keine Schule hinterlegt. */
  readonly childWithoutSchool = signal(false);
  /** Aktuelle Auswahl als Signal — markiert die gewählte Karte. */
  readonly selectedSubject = signal('');

  readonly childOptions = computed(() =>
    this.children().map((child) => ({
      value: ContactFormComponent.CHILD_PREFIX + child.id,
      firstName: child.firstName,
      fullName: [child.firstName, child.lastName].filter(Boolean).join(' '),
      schoolLabel: schoolDisplayName(
        this.schools().find((school) => school.id === child.schoolId)
      ),
    }))
  );

  readonly schoolHint = $localize`:@@home.contact-form.schoolHint:Damit Ihre Anfrage direkt beim richtigen Standort landet.`;
  readonly schoolRequiredMessage = $localize`:@@home.contact-form.schoolRequired:Bitte wählen Sie eine Schule aus – oder setzen Sie den Haken darunter.`;
  readonly generalOptionLabel = $localize`:@@home.contact-form.noSpecificSchool:Keine bestimmte Schule / allgemeine Frage`;

  readonly nameErrorId =
    'name-error-' + Math.random().toString(36).substring(2, 11);
  readonly emailErrorId =
    'email-error-' + Math.random().toString(36).substring(2, 11);
  readonly topicErrorId =
    'topic-error-' + Math.random().toString(36).substring(2, 11);
  readonly messageErrorId =
    'message-error-' + Math.random().toString(36).substring(2, 11);

  // Localized text properties
  nameInputAriaLabel = $localize`:@@home.contactForm.nameInputAriaLabel:Name eingeben`;
  namePlaceholder = $localize`:@@home.contactForm.namePlaceholder:Geben Sie Ihren Namen ein`;
  emailInputAriaLabel = $localize`:@@home.contactForm.emailInputAriaLabel:Email eingeben`;
  emailPlaceholder = $localize`:@@home.contactForm.emailPlaceholder:Geben Sie Ihre E-Mail ein`;
  categorySelectAriaLabel = $localize`:@@home.contactForm.categorySelectAriaLabel:Kategorie auswählen`;
  categoryPlaceholder = $localize`:@@home.contactForm.categoryPlaceholder:Wählen Sie eine Kategorie aus`;
  messageInputAriaLabel = $localize`:@@home.contactForm.messageInputAriaLabel:Nachricht eingeben`;
  messagePlaceholder = $localize`:@@home.contactForm.messagePlaceholder:Geben Sie Ihre Nachricht ein`;
  referenceNumberInputAriaLabel = $localize`:@@home.contactForm.referenceNumberInputAriaLabel:Antragsnummer eingeben`;
  referenceNumberPlaceholder = $localize`:@@home.contactForm.referenceNumberPlaceholder:z.B. AB23CD`;

  contactForm: FormGroup;

  topics = [
    {
      value: 'Feedback',
      label: $localize`:@@home.contact-form.topic.feedback:Feedback`,
    },
    {
      value: 'Problem',
      label: $localize`:@@home.contact-form.topic.problem:Problem`,
    },
    {
      value: 'Rückfrage',
      label: $localize`:@@home.contact-form.topic.inquiry:Rückfrage`,
    },
    {
      value: 'Zahlung',
      label: $localize`:@@home.contact-form.topic.payment:Zahlung`,
    },
    {
      value: 'Sonstiges',
      label: $localize`:@@home.contact-form.topic.other:Sonstiges`,
    },
  ];

  constructor() {
    this.contactForm = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required, emailFormatValidator()]],
      category: ['', [Validators.required]],
      subject: [{ value: '', disabled: true }, [Validators.required]],
      school: this.fb.control<School | string | null>(null, [
        Validators.required,
        schoolSelectedValidator(),
      ]),
      noSchool: [false],
      referenceNumber: ['', [referenceNumberValidator()]],
      message: ['', [Validators.required, Validators.minLength(10)]],
      // privacyConsent: [false, [Validators.requiredTrue]], // deaktiviert (AI im ersten Rollout aus)
    });

    this.subjectControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateSchoolControls());
    this.noSchoolControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateSchoolControls());
  }

  get schoolControl(): FormControl<School | string | null> {
    return this.contactForm.get('school') as FormControl<School | string | null>;
  }

  get subjectControl(): AbstractControl {
    return this.contactForm.get('subject') as AbstractControl;
  }

  get noSchoolControl(): AbstractControl {
    return this.contactForm.get('noSchool') as AbstractControl;
  }

  /**
   * Schaltet Kind-Auswahl und Schulsuche passend zum Modus an oder aus.
   * Abgeschaltete Controls zählen bei der Validierung nicht mit — so ist
   * die Schule nur dort Pflicht, wo sie auch abgefragt wird.
   */
  private updateSchoolControls(): void {
    const quiet = { emitEvent: false };
    const withChildren = this.schoolMode() === 'children';

    if (withChildren) {
      this.subjectControl.enable(quiet);
    } else {
      this.subjectControl.disable(quiet);
    }

    const subject: string = this.subjectControl.value ?? '';
    const childWithoutSchool =
      withChildren &&
      subject.startsWith(ContactFormComponent.CHILD_PREFIX) &&
      !this.findChild(subject)?.schoolId;
    const searchActive = withChildren
      ? subject === this.subjectOtherSchool || childWithoutSchool
      : !this.noSchoolControl.value;
    if (searchActive) {
      this.schoolControl.enable(quiet);
    } else {
      this.schoolControl.reset(null, quiet);
      this.schoolControl.disable(quiet);
    }

    this.childWithoutSchool.set(childWithoutSchool);
    this.selectedSubject.set(subject);
    this.showChildSchoolSearch.set(withChildren && searchActive);
  }

  /** Link unter den Kinder-Karten: Anfrage zu einer Schule ohne eigenes Kind. */
  chooseOtherSchool(): void {
    this.subjectControl.setValue(this.subjectOtherSchool);
    this.subjectControl.markAsTouched();
    setTimeout(() =>
      this.document
        .querySelector<HTMLInputElement>('[data-testid="contact-school"]')
        ?.focus()
    );
  }

  /** „Keine bestimmte Schule“ aus der Trefferliste gewählt. */
  onGeneralSelected(): void {
    if (this.schoolMode() === 'children') {
      this.subjectControl.setValue(this.subjectGeneral);
    } else {
      this.noSchoolControl.setValue(true);
    }
  }

  private async loadChildren(uid: string): Promise<void> {
    try {
      const children = await this.contactService.loadOwnChildren(uid);
      this.children.set(children);
      this.schoolMode.set(children.length > 0 ? 'children' : 'search');
    } catch (error) {
      // Ohne Kinderliste bleibt die Schulsuche — das Formular bleibt bedienbar.
      console.error('Kinder für das Kontaktformular nicht geladen:', error);
      this.schoolMode.set('search');
    }
    if (this.schoolMode() === 'children') {
      this.resetSchoolSelection();
    } else {
      // Einen schon gesetzten Haken nicht wieder wegnehmen.
      this.updateSchoolControls();
    }
  }

  /** Grundzustand der Schulabfrage; bei nur einem Kind ist es schon gewählt. */
  private resetSchoolSelection(): void {
    const children = this.children();
    const quiet = { emitEvent: false };
    this.noSchoolControl.setValue(false, quiet);
    this.subjectControl.setValue(
      children.length === 1
        ? ContactFormComponent.CHILD_PREFIX + children[0].id
        : '',
      quiet
    );
    this.updateSchoolControls();
  }

  private findChild(subject: string): ContactChild | undefined {
    if (!subject.startsWith(ContactFormComponent.CHILD_PREFIX)) {
      return undefined;
    }
    const childId = subject.slice(ContactFormComponent.CHILD_PREFIX.length);
    return this.children().find((child) => child.id === childId);
  }

  /** Schule fürs Ticket — aus dem gewählten Kind oder aus der Suche. */
  private resolveFacility(): { facilityId: string; facilityName: string } {
    const subject: string = this.subjectControl.enabled
      ? this.subjectControl.value ?? ''
      : '';

    // Kind ohne hinterlegte Schule fällt unten auf die Schulsuche zurück.
    const schoolId = this.findChild(subject)?.schoolId;
    if (schoolId) {
      const school = this.schools().find((s) => s.id === schoolId);
      // Fehlt der Name (Liste nicht geladen), ergänzt ihn der Server.
      return { facilityId: schoolId, facilityName: school?.schoolName ?? '' };
    }

    const school = this.schoolControl.enabled ? this.schoolControl.value : null;
    return isSchool(school)
      ? { facilityId: school.id ?? '', facilityName: school.schoolName }
      : { facilityId: '', facilityName: '' };
  }

  ngOnInit() {
    this.contactService.showSuccessMessage.set(false);

    // Ab hier ist alles browser-only. `/contact` wird prerendert
    // (RenderMode.Prerender), ngOnInit läuft dabei in Node: reCAPTCHA und
    // Firebase Auth haben dort keinen Kontext, und der Firestore-Read der
    // Schulen wird ohne App-Check-Token mit `permission-denied` abgelehnt.
    // Ohne Guard wurde daraus eine Unhandled Rejection, die den kompletten
    // Prerender-Worker samt der übrigen Routen umgerissen hat.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.executeRecaptcha();
    this.watchMessageForFaqHints();

    // Firebase Auth lädt currentUser asynchron aus IndexedDB.
    // authStateReady() wartet auf den ersten Auth-State-Resolve.
    this.auth.authStateReady().then(() => {
      const user = this.auth.currentUser;
      const userEmail = user?.email ?? null;
      if (userEmail) {
        this.loggedInUserEmail.set(userEmail);
        this.contactForm.patchValue({ email: userEmail });
      }
      if (user) {
        void this.loadChildren(user.uid);
      } else {
        this.schoolMode.set('search');
        this.resetSchoolSelection();
      }
    });

    this.schoolService
      .loadAllSchools()
      .then(() => {
        this.schools.set(this.schoolService.schools());
      })
      .catch((error) => {
        // Zweite Sicherung: das Formular bleibt ohne Schulliste bedienbar,
        // ein fehlgeschlagener Read darf den Render nie abbrechen.
        console.error('Schulliste konnte nicht geladen werden:', error);
      });
  }

  /**
   * Blendet passende FAQ-Hinweise unter dem Nachrichtenfeld ein (#1071).
   *
   * Läuft ausschliesslich im Browser, entprellt und ohne Netzaufruf: Der Text
   * der Nachricht wird weder ausgewertet noch übertragen. Das Absenden bleibt
   * jederzeit möglich.
   */
  private watchMessageForFaqHints(): void {
    const messageControl = this.contactForm.get('message');
    if (!messageControl) {
      return;
    }
    this.messageSubscription = messageControl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((value: string | null) => {
        const message = value ?? '';
        this.matchedHintRules.set(matchFaqHintRules(message, FAQ_HINT_RULES));
        this.callbackHintMatches.set(
          matchesCallbackWithoutDetails(message, CALLBACK_PATTERNS)
        );
      });
  }

  /** Blendet die aktuell gezeigten Hinweise aus; sie kommen nicht wieder. */
  dismissHints(): void {
    const dismissed = new Set(this.dismissedHintIds());
    for (const rule of this.matchedHintRules()) {
      dismissed.add(rule.id);
    }
    if (this.callbackHintMatches()) {
      dismissed.add(ContactFormComponent.CALLBACK_HINT_ID);
    }
    this.dismissedHintIds.set(dismissed);
  }

  private resetHints(): void {
    this.matchedHintRules.set([]);
    this.callbackHintMatches.set(false);
    this.dismissedHintIds.set(new Set());
  }

  ngOnDestroy() {
    if (this.recaptchaSubscription) {
      this.recaptchaSubscription.unsubscribe();
    }

    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }

    const recaptchaBadge = this.document.querySelector('.grecaptcha-badge');
    if (recaptchaBadge?.parentElement) {
      recaptchaBadge.parentElement.remove();
    }
  }

  private async executeRecaptcha(): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        this.recaptchaSubscription = this.recaptchaV3Service
          .execute('submit_contact')
          .subscribe((token) => {
            this.recaptchaToken.set(token);
            resolve(token);
          });
      } catch (error) {
        console.error('reCAPTCHA error:', error);
        reject(error);
      }
    });
  }

  async onSubmit(): Promise<void> {
    // Solange die Schulauswahl lädt, steht noch nicht fest, was Pflicht ist.
    // Sichtbar ist der Ladehinweis im Formular, angesagt wird er hier.
    if (this.schoolMode() === 'loading') {
      this.screenReaderAnnouncement = $localize`:@@home.contact-form.schoolLoading:Schulauswahl wird geladen …`;
      return;
    }
    if (this.contactForm.valid) {
      this.loadingService.setLoading(true);

      try {
        const token = await this.executeRecaptcha();
        const formValue = this.contactForm.value;

        const { facilityId, facilityName } = this.resolveFacility();

        if (
          await this.contactService.submitContactForm(
            {
              ...formValue,
              referenceNumber: buildReferenceNumber(formValue.referenceNumber),
              facilityId,
              facilityName,
            },
            token
          )
        ) {
          this.contactForm.reset();
          this.resetSchoolSelection();
          this.recaptchaToken.set(null);
          this.resetHints();
          this.screenReaderAnnouncement = $localize`:@@home.contact-form.submitSuccess:Ihre Nachricht wurde erfolgreich gesendet.`;
          this.contactService.showSuccessMessage.set(true);

          // Scroll to top when success message is shown
          if (isPlatformBrowser(this.platformId)) {
            setTimeout(() => {
              this.document.defaultView?.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
            }, 100);
          }

          setTimeout(() => {
            this.screenReaderAnnouncement = '';
          }, 3000);
        }
      } catch (error) {
        console.error('Form submission error:', error);
        this.markFormAsTouched();
        this.screenReaderAnnouncement = $localize`:@@home.contact-form.submitError:Ein Fehler ist aufgetreten. Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.`;
      } finally {
        this.loadingService.setLoading(false);
      }
    } else {
      this.markFormAsTouched();

      // Ansage und Fokus nennen dasselbe Feld: das erste fehlerhafte in Lesereihenfolge (#820)
      const firstInvalid = this.firstInvalidField();
      const firstError = firstInvalid?.closest('lib-school-autocomplete')
        ? 'school'
        : firstInvalid?.closest('[formcontrolname]')?.getAttribute('formcontrolname');

      if (firstError) {
        const fieldNames = {
          name: $localize`:@@home.contact-form.fieldName:Name`,
          email: $localize`:@@home.contact-form.fieldEmail:E-Mail`,
          category: $localize`:@@home.contact-form.fieldTopic:Kategorie`,
          subject: $localize`:@@home.contact-form.fieldSubject:Worum geht es?`,
          school: $localize`:@@home.contact-form.fieldSchool:Schule`,
          message: $localize`:@@home.contact-form.fieldMessage:Nachricht`,
          // privacyConsent: $localize`:@@home.contact-form.fieldPrivacyConsent:Datenschutz-Einwilligung`,
        };

        const errorFieldName =
          fieldNames[firstError as keyof typeof fieldNames];

        const errorMessage = $localize`:@@home.contact-form.fieldCheckError.message:Bitte überprüfen Sie das Feld`;
        this.screenReaderAnnouncement = `${errorMessage} ${errorFieldName}`;
        setTimeout(() => {
          this.screenReaderAnnouncement = '';
        }, 3000);
      }
      firstInvalid?.focus();
    }
  }

  /**
   * Erstes fehlerhaftes Feld in Lesereihenfolge. Nach einem Absenden mit Fehlern
   * springt der Fokus dorthin, damit Tastatur- und Screenreader-Nutzer dort weitermachen (#820).
   */
  private firstInvalidField(): HTMLElement | null {
    return this.host.nativeElement.querySelector<HTMLElement>(
      'input.ng-invalid, textarea.ng-invalid, mat-select.ng-invalid'
    );
  }

  get referenceNumberControl() {
    return this.contactForm.get('referenceNumber');
  }

  onReferenceNumberPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    if (!pasted) {
      return;
    }
    event.preventDefault();
    this.referenceNumberControl?.setValue(normalizeReferenceNumberBody(pasted));
  }

  onReferenceNumberBlur(): void {
    const current = this.referenceNumberControl?.value ?? '';
    if (!current) {
      return;
    }
    const normalized = normalizeReferenceNumberBody(current);
    if (normalized !== current) {
      this.referenceNumberControl?.setValue(normalized);
    }
  }

  back(): void {
    this.router.navigate(['/']);
  }

  private markFormAsTouched(): void {
    Object.values(this.contactForm.controls).forEach((control) => {
      control.markAsTouched();
    });
  }
}
